// reportService.js - ✅ CORRIGÉ avec utilisation des services existants

import { apiCall } from './api';

/**
 * 🔧 FONCTIONS UTILITAIRES POUR LE MAPPING DES DONNÉES
 */
const reportUtils = {
  // ✅ Fonction pour obtenir le label des méthodes de paiement (CORRIGÉ)
  getPaymentMethodLabel: (method) => {
    const labels = {
      'CASH': 'Espèces',
      'CREDIT_CARD': 'Carte de crédit',
      'DEBIT_CARD': 'Carte de débit',
      'MOBILE_MONEY': 'Mobile Money',
      'BANK_TRANSFER': 'Virement bancaire',
      'CHECK': 'Chèque',
      'CREDIT': 'À crédit'
    };
    return labels[method] || method || 'Inconnu';
  },

  // ✅ Normaliser les réponses API pour s'assurer qu'on a un tableau (CORRIGÉ)
  ensureArray: (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (typeof data === 'object') {
      // Si c'est un objet avec une propriété qui contient le tableau
      const possibleArrays = ['data', 'results', 'items', 'content'];
      for (const key of possibleArrays) {
        if (data[key] && Array.isArray(data[key])) {
          return data[key];
        }
      }
      // Si c'est un objet unique, le mettre dans un tableau
      return [data];
    }
    return [];
  },

  // Vérifier si une réponse API est valide
  isValidResponse: (response) => {
    return response && response.success && response.data !== undefined;
  }
};

/**
 * Service de reporting utilisant les endpoints et services existants
 */
const reportService = {

  /**
   * 📊 STATISTIQUES GÉNÉRALES DE VENTES
   * ✅ Utilise l'endpoint existant /manager/stats/today
   */
  getSalesStats: async (filters = {}) => {
    try {
      console.log('📊 Récupération statistiques de ventes...', filters);

      const todayResponse = await apiCall.get('/manager/stats/today');
      
      if (reportUtils.isValidResponse(todayResponse)) {
        const todayData = todayResponse.data;
        
        return {
          success: true,
          data: {
            totalRevenue: todayData.total_revenue || 0,
            totalSales: todayData.sale_count || 0,
            totalCustomers: todayData.unique_customers || 0,
            averageOrderValue: todayData.average_sale || 0,
            revenueGrowth: 0, // À calculer si nécessaire
            salesGrowth: 0,
            customerGrowth: 0
          }
        };
      }

      return {
        success: true,
        data: {
          totalRevenue: 0,
          totalSales: 0,
          totalCustomers: 0,
          averageOrderValue: 0,
          revenueGrowth: 0,
          salesGrowth: 0,
          customerGrowth: 0
        }
      };

    } catch (error) {
      console.error('❌ Erreur getSalesStats:', error);
      return {
        success: true,
        data: {
          totalRevenue: 0,
          totalSales: 0,
          totalCustomers: 0,
          averageOrderValue: 0,
          revenueGrowth: 0,
          salesGrowth: 0,
          customerGrowth: 0
        }
      };
    }
  },

  /**
   * 📈 DONNÉES DE REVENUS PAR PÉRIODE
   * ✅ Utilise les ventes existantes depuis /employee/sales
   */
  getRevenueData: async (filters = {}) => {
    try {
      console.log('📊 Récupération données de revenus...', filters);

      const salesResponse = await apiCall.get('/employee/sales');
      
      if (reportUtils.isValidResponse(salesResponse)) {
        const salesArray = reportUtils.ensureArray(salesResponse.data);
        
        // Grouper les ventes par date
        const salesByDate = {};
        
        salesArray.forEach(sale => {
          if (sale.created_at || sale.createdAt) {
            const date = new Date(sale.created_at || sale.createdAt).toISOString().split('T')[0];
            if (!salesByDate[date]) {
              salesByDate[date] = { revenue: 0, sales: 0 };
            }
            salesByDate[date].revenue += parseFloat(sale.total || 0);
            salesByDate[date].sales += 1;
          }
        });

        // Convertir en array et trier
        const revenueData = Object.entries(salesByDate)
          .map(([date, data]) => ({
            date,
            revenue: data.revenue,
            sales: data.sales
          }))
          .sort((a, b) => new Date(a.date) - new Date(b.date))
          .slice(-7); // Derniers 7 jours

        return {
          success: true,
          data: revenueData
        };
      }

      return { success: true, data: [] };

    } catch (error) {
      console.error('❌ Erreur getRevenueData:', error);
      return { success: true, data: [] };
    }
  },

  /**
   * 🏆 TOP PRODUITS LES PLUS VENDUS
   * ✅ Calcule depuis les ventes existantes
   */
  getTopProducts: async (filters = {}) => {
    try {
      console.log('🏆 Récupération top produits...', filters);

      const salesResponse = await apiCall.get('/employee/sales');
      
      if (reportUtils.isValidResponse(salesResponse)) {
        const salesArray = reportUtils.ensureArray(salesResponse.data);
        
        // Analyser les ventes pour extraire les produits populaires
        const productStats = {};
        
        salesArray.forEach(sale => {
          if (sale.items && Array.isArray(sale.items)) {
            sale.items.forEach(item => {
              const productId = item.product_id || item.productId;
              const productName = item.product_name || item.productName || 'Produit';
              
              if (!productStats[productId]) {
                productStats[productId] = {
                  productId,
                  productName,
                  categoryName: item.category_name || 'N/A',
                  totalSold: 0,
                  totalRevenue: 0,
                  saleCount: 0,
                  currentStock: item.current_stock || 0
                };
              }
              
              productStats[productId].totalSold += item.quantity || 0;
              productStats[productId].totalRevenue += (item.quantity || 0) * (item.unit_price || 0);
              productStats[productId].saleCount += 1;
            });
          }
        });

        // Convertir en array et trier
        const topProducts = Object.values(productStats)
          .sort((a, b) => b.totalRevenue - a.totalRevenue)
          .slice(0, filters.limit || 10);

        return {
          success: true,
          data: topProducts
        };
      }

      return { success: true, data: [] };

    } catch (error) {
      console.error('❌ Erreur getTopProducts:', error);
      return { success: true, data: [] };
    }
  },

  /**
   * 👥 TOP CLIENTS
   * ✅ Calcule depuis les ventes existantes
   */
  getTopCustomers: async (filters = {}) => {
    try {
      console.log('👥 Récupération top clients...', filters);

      const salesResponse = await apiCall.get('/employee/sales');
      
      if (reportUtils.isValidResponse(salesResponse)) {
        const salesArray = reportUtils.ensureArray(salesResponse.data);
        
        // Analyser les ventes par client
        const customerStats = {};
        
        salesArray.forEach(sale => {
          const customerId = sale.customer_id || sale.customerId || 'anonymous';
          const customerName = sale.customer_name || sale.customerName || 'Client anonyme';
          
          if (!customerStats[customerId]) {
            customerStats[customerId] = {
              customerId,
              customerName,
              totalSpent: 0,
              orderCount: 0,
              lastPurchase: null,
              averageOrderValue: 0
            };
          }
          
          customerStats[customerId].totalSpent += parseFloat(sale.total || 0);
          customerStats[customerId].orderCount += 1;
          
          const saleDate = sale.created_at || sale.createdAt;
          if (saleDate) {
            const purchaseDate = new Date(saleDate);
            if (!customerStats[customerId].lastPurchase || 
                purchaseDate > new Date(customerStats[customerId].lastPurchase)) {
              customerStats[customerId].lastPurchase = saleDate;
            }
          }
        });

        // Calculer la moyenne et trier
        const topCustomers = Object.values(customerStats)
          .map(customer => ({
            ...customer,
            averageOrderValue: customer.orderCount > 0 ? 
              customer.totalSpent / customer.orderCount : 0
          }))
          .sort((a, b) => b.totalSpent - a.totalSpent)
          .slice(0, filters.limit || 10);

        return {
          success: true,
          data: topCustomers
        };
      }

      return { success: true, data: [] };

    } catch (error) {
      console.error('❌ Erreur getTopCustomers:', error);
      return { success: true, data: [] };
    }
  },

  /**
   * 💳 STATISTIQUES DES MÉTHODES DE PAIEMENT
   * ✅ CORRIGÉ : Utilise la fonction utilitaire pour les labels
   */
  getPaymentMethodsStats: async (filters = {}) => {
    try {
      console.log('💳 Récupération stats méthodes paiement...', filters);

      const salesResponse = await apiCall.get('/employee/sales');
      
      if (reportUtils.isValidResponse(salesResponse)) {
        const salesArray = reportUtils.ensureArray(salesResponse.data);
        
        // Analyser les méthodes de paiement
        const paymentStats = {};
        let totalAmount = 0;
        
        salesArray.forEach(sale => {
          const method = sale.payment_method || sale.paymentMethod || 'CASH';
          const amount = parseFloat(sale.total || 0);
          
          if (!paymentStats[method]) {
            paymentStats[method] = {
              method,
              methodLabel: reportUtils.getPaymentMethodLabel(method), // ✅ CORRIGÉ
              amount: 0,
              count: 0
            };
          }
          
          paymentStats[method].amount += amount;
          paymentStats[method].count += 1;
          totalAmount += amount;
        });

        // Calculer les pourcentages
        const paymentMethods = Object.values(paymentStats).map(method => ({
          ...method,
          percentage: totalAmount > 0 ? (method.amount / totalAmount) * 100 : 0
        }));

        return {
          success: true,
          data: paymentMethods
        };
      }

      return { success: true, data: [] };

    } catch (error) {
      console.error('❌ Erreur getPaymentMethodsStats:', error);
      return { success: true, data: [] };
    }
  },

  /**
   * 👨‍💼 PERFORMANCE DE L'ÉQUIPE
   * ✅ Calcule depuis les ventes existantes (pas d'endpoint team/activity)
   */
  getTeamPerformance: async (filters = {}) => {
    try {
      console.log('👨‍💼 Récupération performance équipe...', filters);

      const salesResponse = await apiCall.get('/employee/sales');
      
      if (reportUtils.isValidResponse(salesResponse)) {
        const salesArray = reportUtils.ensureArray(salesResponse.data);
        
        // Grouper par vendeur
        const employeeStats = {};
        
        salesArray.forEach(sale => {
          const employeeId = sale.seller_id || sale.user_id || sale.sellerId || 'unknown';
          let employeeName = 'Employé';
          
          // Essayer différents formats de nom
          if (sale.seller_name) {
            employeeName = sale.seller_name;
          } else if (sale.seller) {
            const firstName = sale.seller.first_name || sale.seller.firstName || sale.seller.prenom || '';
            const lastName = sale.seller.last_name || sale.seller.lastName || sale.seller.nom || '';
            employeeName = `${firstName} ${lastName}`.trim() || 'Employé';
          }
          
          if (!employeeStats[employeeId]) {
            employeeStats[employeeId] = {
              employeeId,
              sellerName: employeeName,
              sales: 0,
              revenue: 0,
              target: 50000 // Objectif par défaut
            };
          }
          
          employeeStats[employeeId].sales += 1;
          employeeStats[employeeId].revenue += parseFloat(sale.total || 0);
        });

        // Calculer les performances et classer
        const teamPerformance = Object.values(employeeStats)
          .map((member, index) => ({
            ...member,
            ranking: index + 1,
            achievement: member.target > 0 ? (member.revenue / member.target) * 100 : 0,
            progressPercent: Math.min(100, member.target > 0 ? (member.revenue / member.target) * 100 : 0)
          }))
          .sort((a, b) => b.revenue - a.revenue)
          .map((member, index) => ({
            ...member,
            ranking: index + 1 // Réassigner le rang après tri
          }));

        return {
          success: true,
          data: teamPerformance
        };
      }

      return { success: true, data: [] };

    } catch (error) {
      console.error('❌ Erreur getTeamPerformance:', error);
      return { success: true, data: [] };
    }
  },

  /**
   * ⚠️ ALERTES DE STOCK
   * ✅ CORRIGÉ : Gestion robuste des réponses API avec ensureArray
   */
  getStockAlerts: async (filters = {}) => {
    try {
      console.log('⚠️ Récupération alertes stock...', filters);

      // Essayer l'endpoint dédié aux alertes si disponible
      try {
        const alertsResponse = await apiCall.get('/manager/products/stock/alerts');
        
        if (reportUtils.isValidResponse(alertsResponse)) {
          const alertsArray = reportUtils.ensureArray(alertsResponse.data);
          return {
            success: true,
            data: alertsArray.map(alert => ({
              productId: alert.product_id || alert.productId,
              productName: alert.product_name || alert.productName || 'Produit',
              categoryName: alert.category_name || alert.categoryName || 'N/A',
              currentStock: alert.current_stock || alert.currentStock || 0,
              minThreshold: alert.min_threshold || alert.minThreshold || 0,
              maxThreshold: alert.max_threshold || alert.maxThreshold || 0,
              status: alert.alert_level || alert.status || 'LOW'
            }))
          };
        }
      } catch (alertsError) {
        console.warn('⚠️ Endpoint stock/alerts indisponible, calcul depuis les produits');
      }

      // Fallback: calculer depuis les produits
      const productsResponse = await apiCall.get('/manager/products');
      
      if (reportUtils.isValidResponse(productsResponse)) {
        // ✅ CORRIGÉ : S'assurer qu'on a un tableau avec ensureArray
        const productsArray = reportUtils.ensureArray(productsResponse.data);
        console.log('📦 Produits récupérés pour alertes:', productsArray.length);
        
        // Filtrer les produits avec stock faible
        const lowStockProducts = productsArray.filter(product => {
          const currentStock = product.current_stock || product.currentStock || 0;
          const minThreshold = product.min_threshold || product.minThreshold || 5;
          return currentStock <= minThreshold;
        });

        const stockAlerts = lowStockProducts.map(product => ({
          productId: product.id || product.productId,
          productName: product.name || product.productName || 'Produit',
          categoryName: product.category_name || product.categoryName || 'N/A',
          currentStock: product.current_stock || product.currentStock || 0,
          minThreshold: product.min_threshold || product.minThreshold || 5,
          maxThreshold: product.max_threshold || product.maxThreshold || 100,
          status: (product.current_stock || 0) === 0 ? 'OUT_OF_STOCK' : 'LOW'
        }));

        console.log('✅ Alertes calculées depuis produits:', stockAlerts.length);

        return {
          success: true,
          data: stockAlerts
        };
      }

      return { success: true, data: [] };

    } catch (error) {
      console.error('❌ Erreur getStockAlerts:', error);
      return { success: true, data: [] };
    }
  },

  /**
   * 📊 STATISTIQUES PAR CATÉGORIE
   */
  getCategoryStats: async (filters = {}) => {
    try {
      console.log('📊 Récupération stats catégories...', filters);

      const salesResponse = await apiCall.get('/employee/sales');
      
      if (reportUtils.isValidResponse(salesResponse)) {
        const salesArray = reportUtils.ensureArray(salesResponse.data);
        
        // Analyser par catégories
        const categoryStats = {};
        
        salesArray.forEach(sale => {
          if (sale.items && Array.isArray(sale.items)) {
            sale.items.forEach(item => {
              const categoryName = item.category_name || item.categoryName || 'Non classé';
              
              if (!categoryStats[categoryName]) {
                categoryStats[categoryName] = {
                  categoryName,
                  totalSales: 0,
                  totalRevenue: 0,
                  productCount: new Set()
                };
              }
              
              categoryStats[categoryName].totalSales += item.quantity || 0;
              categoryStats[categoryName].totalRevenue += (item.quantity || 0) * (item.unit_price || 0);
              categoryStats[categoryName].productCount.add(item.product_id || item.productId);
            });
          }
        });

        // Convertir en array
        const categories = Object.values(categoryStats).map(cat => ({
          categoryName: cat.categoryName,
          totalSales: cat.totalSales,
          totalRevenue: cat.totalRevenue,
          productCount: cat.productCount.size
        }));

        return {
          success: true,
          data: categories
        };
      }

      return { success: true, data: [] };

    } catch (error) {
      console.error('❌ Erreur getCategoryStats:', error);
      return { success: true, data: [] };
    }
  },

  /**
   * 💰 STATISTIQUES CRÉDIT
   */
  getCreditStats: async (filters = {}) => {
    try {
      console.log('💰 Récupération stats crédit...', filters);

      const salesResponse = await apiCall.get('/employee/sales');
      
      if (reportUtils.isValidResponse(salesResponse)) {
        const salesArray = reportUtils.ensureArray(salesResponse.data);
        
        // Filtrer les ventes à crédit
        const creditSales = salesArray.filter(sale => 
          sale.payment_method === 'CREDIT' || 
          sale.paymentMethod === 'CREDIT' ||
          sale.payment_status === 'UNPAID' ||
          sale.paymentStatus === 'UNPAID'
        );

        const paidCredits = creditSales.filter(sale => 
          sale.payment_status === 'PAID' || 
          sale.paymentStatus === 'PAID'
        );

        const totalCredits = creditSales.reduce((sum, sale) => sum + parseFloat(sale.total || 0), 0);
        const totalPaid = paidCredits.reduce((sum, sale) => sum + parseFloat(sale.total || 0), 0);

        return {
          success: true,
          data: {
            totalCreditAmount: totalCredits,
            totalPaidAmount: totalPaid,
            unpaidAmount: totalCredits - totalPaid,
            creditSalesCount: creditSales.length,
            paidCreditsCount: paidCredits.length,
            averageCreditAmount: creditSales.length > 0 ? totalCredits / creditSales.length : 0,
            paymentRate: creditSales.length > 0 ? (paidCredits.length / creditSales.length) * 100 : 0
          }
        };
      }

      return {
        success: true,
        data: {
          totalCreditAmount: 0,
          totalPaidAmount: 0,
          unpaidAmount: 0,
          creditSalesCount: 0,
          paidCreditsCount: 0,
          averageCreditAmount: 0,
          paymentRate: 0
        }
      };

    } catch (error) {
      console.error('❌ Erreur getCreditStats:', error);
      return {
        success: true,
        data: {
          totalCreditAmount: 0,
          totalPaidAmount: 0,
          unpaidAmount: 0,
          creditSalesCount: 0,
          paidCreditsCount: 0,
          averageCreditAmount: 0,
          paymentRate: 0
        }
      };
    }
  },

  /**
   * 📊 RAPPORT COMPLET POUR LE DASHBOARD
   * ✅ Combine toutes les données avec gestion d'erreurs robuste
   */
  getCompleteDashboardReport: async (filters = {}) => {
    try {
      console.log('📊 Génération rapport complet dashboard...', filters);

      // Exécuter toutes les requêtes en parallèle avec Promise.allSettled
      const [
        salesStats,
        revenueData,
        topProducts,
        topCustomers,
        paymentMethods,
        teamPerformance,
        stockAlerts,
        categoryStats,
        creditStats
      ] = await Promise.allSettled([
        reportService.getSalesStats(filters),
        reportService.getRevenueData(filters),
        reportService.getTopProducts({ ...filters, limit: 10 }),
        reportService.getTopCustomers({ ...filters, limit: 10 }),
        reportService.getPaymentMethodsStats(filters),
        reportService.getTeamPerformance(filters),
        reportService.getStockAlerts({ ...filters, alertLevel: 'all' }),
        reportService.getCategoryStats(filters),
        reportService.getCreditStats(filters)
      ]);

      // Construire les données du dashboard en gérant les erreurs
      const dashboardData = {
        salesStats: salesStats.status === 'fulfilled' && salesStats.value.success ? 
          salesStats.value.data : {
            totalRevenue: 0, totalSales: 0, totalCustomers: 0, averageOrderValue: 0,
            revenueGrowth: 0, salesGrowth: 0, customerGrowth: 0
          },
        revenueData: revenueData.status === 'fulfilled' && revenueData.value.success ? 
          revenueData.value.data : [],
        topProducts: topProducts.status === 'fulfilled' && topProducts.value.success ? 
          topProducts.value.data : [],
        topCustomers: topCustomers.status === 'fulfilled' && topCustomers.value.success ? 
          topCustomers.value.data : [],
        paymentMethods: paymentMethods.status === 'fulfilled' && paymentMethods.value.success ? 
          paymentMethods.value.data : [],
        teamPerformance: teamPerformance.status === 'fulfilled' && teamPerformance.value.success ? 
          teamPerformance.value.data : [],
        stockAlerts: stockAlerts.status === 'fulfilled' && stockAlerts.value.success ? 
          stockAlerts.value.data : [],
        categoryStats: categoryStats.status === 'fulfilled' && categoryStats.value.success ? 
          categoryStats.value.data : [],
        creditStats: creditStats.status === 'fulfilled' && creditStats.value.success ? 
          creditStats.value.data : null
      };

      console.log('✅ Rapport complet généré avec succès');

      return {
        success: true,
        data: dashboardData
      };

    } catch (error) {
      console.error('❌ Erreur génération rapport complet:', error);
      return {
        success: true,
        data: {
          salesStats: { totalRevenue: 0, totalSales: 0, totalCustomers: 0, averageOrderValue: 0 },
          revenueData: [],
          topProducts: [],
          topCustomers: [],
          paymentMethods: [],
          teamPerformance: [],
          stockAlerts: [],
          categoryStats: [],
          creditStats: null
        }
      };
    }
  }
};

export default reportService;